<template>
  <q-page :padding="!operatorId && $q.screen.gt.xs" class="docs-table" :style-fn="myTweak">
    <q-table
        :title="$t('rebit-accounts.menu.list')"
        ref="table"
        :data="displayableData"
        :columns="columns"
        :filter="filter"
        row-key="id"
        :pagination.sync="serverPagination"
        :visible-columns="visibleColumns"
        :loading="loading"
        :selected.sync="selectedRows"
        :rows-per-page-options="[10, 15, 30, 50, 100]"
        style="margin-bottom: 20px;"
        @request="all"
    >
      <template v-slot:top-selection="props" v-if="$auth.can('rebit-accounts.edit')">
        <q-btn color="secondary" outline icon="fa fa-edit" :label="$t('edit')" class="q-mr-sm"
               v-if="selectedRows.length === 1" @click="edit(selectedRows[0])"/>
        <q-btn color="pink-5" outline icon="fa fa-times" class="q-mr-sm" @click="selectedRows = []"/>
      </template>

      <template v-slot:top-right="props">
        <q-btn-dropdown
            outline
            color="primary"
            label="View Options"
            class="q-mr-sm"
        >
          <div class="row q-col-gutter-md q-pa-md" style="width: 280px">
            <div class="col col-12">
              <q-checkbox v-model="hide_zero_balances" label="Hide zero balance accounts" color="primary" class="q-ml-xs" />
            </div>
              <div class="col col-12">
              <q-checkbox v-model="hide_inactive_accounts" label="Hide Inactive accounts" color="primary" class="q-ml-xs" />
            </div>
          </div>
        </q-btn-dropdown>

        <q-btn
            outline
            label="New"
            icon="fa fa-plus"
            color="positive"
            class="q-mr-sm"
            v-if="$auth.can('rebit-accounts.create')"
            @click="$router.push({name: 'rebit-accounts.create'})"
        /> <!-- NEW -->
      	<q-btn outline :label="$t('refresh')" icon="fas fa-sync" color="primary" class="q-mr-sm" @click="all({pagination: serverPagination, filter})" :loading="loading">
			<template v-slot:loading>
		    <q-spinner-dots />
		  </template>
		</q-btn>
        <q-btn
          :outline="!is_filtered"
          color="black"
          :label="is_filtered ? 'Filtered' : 'Filter'"
          class="q-mr-sm"
          icon="fa fa-filter"
        >
          <q-menu>
            <div class="row q-pa-md q-col-gutter-md" style="width: 300px">
              <div class="col-12">
                <q-select
                  v-model="currency"
                  label="Currency"
                  clearable
                  dense
                  options-dense
                  emit-value
                  map-options
                  option-value="acronym"
                  option-label="name"
                  :options="currencies"
                  style="min-width: 150px; margin-right:10px"
                  color="primary"
                  @input="all({ pagination: serverPagination, filter})"
                >
                    <template v-slot:option="scope">
                        <q-item
                            v-bind="scope.itemProps"
                            v-on="scope.itemEvents"
                        >
                            <q-item-section>
                                <q-item-label><span class="text-bold">{{scope.opt.symbol}}</span> ({{ scope.opt.name }})</q-item-label>
                            </q-item-section>
                        </q-item>
                    </template>
                </q-select>
              </div>
            </div>
          </q-menu>
        </q-btn>
        <q-input dense debounce="300" v-model="filter" :placeholder="$t('search')" class="q-mr-sm">
          <template v-slot:prepend>
            <q-icon name="search" />
          </template>
        </q-input>
        <q-select
            v-model="visibleColumns"
            multiple
            dense
            options-dense
            :display-value="$q.lang.table.columns"
            emit-value
            map-options
            :options="columns"
            option-value="name"
            style="min-width: 150px; margin-right:10px"
            color="primary"
        />
      </template>

      <!-- START CUSTOM COLUMNS -->
        <template v-slot:header="props">
            <q-tr :props="props">
                <q-th auto-width />
                <q-th
                    v-for="col in columns"
                    :key="col.name"
                    :props="props"
                >
                    <span>{{ col.label }}</span>
                </q-th>
            </q-tr>
        </template>
        <template v-slot:body="props">
            <q-tr :props="props">
                <q-td auto-width>
                    <q-btn size="sm" color="primary" round dense @click="details(props)" :icon="props.expand ? 'fa fa-angle-up' : 'fa fa-angle-down'"/>
                </q-td>
                <q-td key="id" :props="props">{{ props.row.id }}</q-td>
                <q-td key="holder" :props="props" :title="props.row.holder">{{props.row.holder}}</q-td>
                <q-td key="owner_operator" :props="props">
                    {{props.row.owner_operator.name || '-'}}
                </q-td>
                <q-td key="description">{{props.row.description}}</q-td>
                <q-td key="currency" :props="props">
                    <div class="text-bold">{{props.row.currency.symbol}}</div>
                    <small>{{props.row.currency.name}}</small>
                </q-td>
                <q-td key="bank" :props="props">
                    <q-item class="no-padding">
                        <q-item-section avatar>
                            <q-avatar>
                                <q-icon :name="type_icons[props.row.type]" color="primary" size="xs"/>
                            </q-avatar>
                        </q-item-section>
                        <q-item-section>
                            <q-item-label>
                                {{props.row.bank ? props.row.bank : 'N/A'}}
                            </q-item-label>
                        </q-item-section>
                    </q-item>
                </q-td>
                <q-td key="is_active" :props="props">
                    <q-toggle
                        v-model="props.row.is_active"
                        checked-icon="fa fa-check"
                        color="green"
                        @input="editInline($event, props.row.is_active, props.row, 'is_active')"
                        unchecked-icon="fa fa-times"
                        :disabled="loading"
                    />
                </q-td>
                <q-td key="is_depositable" :props="props">
                    <q-toggle
                        v-model="props.row.is_depositable"
                        checked-icon="fa fa-check"
                        color="green"
                        @input="editInline($event, props.row.is_depositable, props.row, 'is_depositable')"
                        unchecked-icon="fa fa-times"
                        :disabled="loading"
                    />
                </q-td>
                <q-td key="opening_balance" :props="props">
                    <currency-format
                        class="text-bold inline-block"
                        :value="props.row.opening_balance"
                        :unit="props.row.currency.symbol"
                        decimal-max-length="4"
                    />
                    <q-popup-edit
                        v-model="props.row.opening_balance"
                        buttons
                        @save="(val, initialValue) => editInline(val, initialValue, props.row, 'opening_balance')"
                        v-if="$auth.can('rebit-accounts.edit')"
                    >
                        <q-input v-model="props.row.opening_balance" type="number" dense autofocus color="primary"/>
                    </q-popup-edit>
                </q-td>
                <q-td key="real_balance" :props="props">
                    <q-btn
                        round
                        outline
                        color="primary"
                        icon="fas fa-sync"
                        size="xs"
                        v-if="props.row.is_api_enabled"
                        @click="syncBalance(props.row)"
                        :loading="syncing"
                    />&nbsp;
                    <span>
                        <currency-format
                            class="text-bold inline-block"
                            :class="{'text-orange': props.row.real_balance < props.row.ideal_balance}"
                            :value="props.row.real_balance"
                            :unit="props.row.currency.symbol"
                            decimal-max-length="4"
                        />
                        <q-popup-edit
                            v-model="props.row.real_balance"
                            buttons
                            @save="(val, initialValue) => editInline(val, initialValue, props.row, 'real_balance')"
                            v-if="$auth.can('rebit-accounts.view_filtered_and_edit_real_balance') || $auth.can('rebit-accounts.edit')"
                        >
                            <q-input v-model="props.row.real_balance" type="number" dense autofocus color="primary"/>
                        </q-popup-edit>
                    </span>
                </q-td>
                <q-td key="ideal_balance" :props="props">
                    <currency-format
                        class="text-bold inline-block"
                        :value="props.row.ideal_balance"
                        :unit="props.row.currency.symbol"
                        decimal-max-length="2"
                    />
                    <q-popup-edit
                        v-model="props.row.ideal_balance"
                        buttons
                        @save="(val, initialValue) => editInline(val, initialValue, props.row, 'ideal_balance')"
                        v-if="$auth.can('rebit-accounts.edit')"
                    >
                        <q-input v-model="props.row.ideal_balance" type="number" dense autofocus color="primary"/>
                    </q-popup-edit>
                </q-td>
                <q-td key="monthly_turnover_limit" :props="props">
                    <currency-format
                        class="text-bold inline-block"
                        :value="props.row.monthly_turnover_limit"
                        :unit="props.row.currency.symbol"
                        decimal-max-length="2"
                        hide-zero-decimal
                    />
                    <q-popup-edit
                        v-model="props.row.monthly_turnover_limit"
                        buttons
                        @save="(val, initialValue) => editInline(val, initialValue, props.row, 'monthly_turnover_limit')"
                        v-if="$auth.can('rebit-accounts.edit')"
                    >
                        <q-input v-model="props.row.monthly_turnover_limit" type="number" dense autofocus color="primary"/>
                    </q-popup-edit>
                </q-td>
                <q-td key="updated_at" :props="props">
                    {{props.row.updated_at}}
                </q-td>
                <q-td key="setting" :props="props" v-if="$auth.can('rebit-accounts.edit')">
                    <q-btn round size="sm" color="orange" icon="fa fa-edit" @click="edit(props.row)"/>&nbsp;
                </q-td>
            </q-tr>
            <q-tr v-show="props.expand" :props="props" transition-show="jump-down" transition-hide="jump-up">
                <q-td colspan="100%">
                    <rebit-account-details v-if="props.expand" :account="props.row"/>
                </q-td>
            </q-tr>
        </template>
      <!-- END CUSTOM COLUMNS -->
    </q-table>

    <div class="row q-col-gutter-md" v-if="!asDetailPage">
        <div class="col-6">
            <total-balances :operator-id="operatorId"/>
        </div>
    </div>

  </q-page>
</template>

<script>
  import RebitAccount from '../models/RebitAccount.js'
  import TotalBalances from '../components/TotalBalances.vue'
  import RebitAccountDetails from './details/RebitAccountDetails.vue'
  import { mapGetters, mapActions } from 'vuex'
  export default {
    name: 'RebitAccountList',
    props: {
      operatorId: {
        type: Number,
        required: false
      },
      asDetailPage: {
        type: Boolean,
        default: false
      }
    },
    computed: {
      ...mapGetters(['currencies']),
	  columns() {
        let columns = [
          {name: 'id', required: true, label: this.$t('rebit-accounts.table_titles.id'), align: 'left', field: 'id', sortable: true},
          {name: 'holder', label: this.$t('rebit-accounts.table_titles.holder'), align: 'left', field: 'holder', sortable: false, classes: 'ellipsis', style: 'max-width: 180px', headerStyle: 'max-width: 180px'},
          {name: 'description', label: this.$t('rebit-accounts.table_titles.description'), align: 'left', field: 'description', sortable: true},
          {name: 'currency', label: this.$t('rebit-accounts.table_titles.currency'), align: 'left', field: 'currency.name', sortable: false},
          {name: 'bank', label: this.$t('rebit-accounts.table_titles.bank'), align: 'left', field: 'bank', sortable: false},
          {name: 'is_active', label: this.$t('rebit-accounts.table_titles.status'), align: 'center', field: 'is_active', sortable: true},
          {name: 'is_depositable', label: this.$t('rebit-accounts.table_titles.is_depositable'), align: 'center', field: 'is_depositable', sortable: true},
          {name: 'opening_balance', label: this.$t('rebit-accounts.table_titles.opening_balance'), align: 'left', field: 'opening_balance', sortable: true},
          {name: 'real_balance', label: this.$t('rebit-accounts.table_titles.real_balance'), align: 'left', field: 'real_balance', sortable: true},
          {name: 'ideal_balance', label: this.$t('rebit-accounts.table_titles.ideal_balance'), align: 'left', field: 'ideal_balance', sortable: true},
          {name: 'monthly_turnover_limit', label: this.$t('rebit-accounts.table_titles.monthly_turnover_limit'), align: 'left', field: 'monthly_turnover_limit', sortable: true},
          {name: 'updated_at', label: this.$t('rebit-accounts.table_titles.updated_at'), align: 'left', field: 'updated_at', sortable: true}
        ]

        if(!this.operatorId) {
          columns.splice(2,0, {name: 'owner_operator', label: this.$t('rebit-accounts.table_titles.owner_operator'), align: 'left', field: 'owner_operator.id', sortable: true})
        }

        // add Edit/Delete column just if the user has appropriate permission
        if(this.$auth.can('rebit-accounts.edit')){
          this.selection = 'multiple'
          this.visibleColumns.push('setting')
          columns.push({name: 'setting', align: 'center', label: this.$t('rebit-accounts.table_titles.setting'), sortable: false, field: 'setting'})
        }
        return columns;
      },
      displayableData () {
        let filtered_data = this.hide_zero_balances ? this.serverData.filter(record => record.real_balance > 0) : this.serverData
        return this.hide_inactive_accounts ? filtered_data.filter(record => record.is_active) : filtered_data
      },
      is_filtered() {
        return this.currency
      },
    },
    data() {
      return {
        separator: 'horizontal',
            filter: '',
        loading: false,
        syncing: false,
        currency: null,
        serverPagination: {
            page: 1,
            sortBy: this.operatorId ? 'real_balance' : 'id',
            rowsPerPage: 30,
            descending: true,
            rowsNumber: 10
        },
        visibleColumns: ['id', 'holder', 'owner_operator', 'bank', 'currency', 'real_balance', 'description', 'updated_at'],
        serverData: [],
        selection: 'none',
        selectedRows: [],
        type_icons: {
            'Account': 'fa fa-university',
            'Wallet address': 'fab fa-btc',
            'Cash': 'fa fa-coins',
        },
        hide_zero_balances: false,
        hide_inactive_accounts: true,
      }
    },
    methods: {
      ...mapActions(['set_currencies']),
      all({pagination, filter}) {
        this.loading = true
        RebitAccount.all(`${this.operatorId ? `operator_id=${this.operatorId}&` : ''}sort=${pagination.sortBy}%7C${pagination.descending ? 'desc' : 'asc'}&page=${pagination.page}&per_page=${pagination.rowsPerPage}&filter=${filter}&currency=${this.currency !== null ? this.currency: ''}`, ({data, meta}) => {
          this.serverPagination = pagination
          // we also set (or update) rowsNumber
          this.serverPagination.rowsNumber = meta.total
          this.serverData = data
          this.loading = false
        }).catch(error => {
          this.$q.notify({
            message: error.data.error,
            color: 'negative',
            icon: 'fa fa-exclamation-triangle'
          })
          this.loading = false
        })
      },
      editInline(val, initVal, row, col) {
          row[col] = initVal
          this.loading = true
          new RebitAccount({
              id: row.id,
              owner_operator_id: row.owner_operator.id,
              label: row.label,
              type: row.type,
              holder: row.holder,
              accountable_id: row.accountable_id,
              currency_acronym: row.currency.acronym,
              network: row.network,
              is_active: row.is_active,
              is_depositable: row.is_depositable,
              opening_balance: col === 'opening_balance' ? val : row.opening_balance,
              real_balance: col === 'real_balance' ? val : row.real_balance,
              ideal_balance: col === 'ideal_balance' ? val : row.ideal_balance,
              monthly_turnover_limit: col === 'monthly_turnover_limit' ? val : row.monthly_turnover_limit
          }).update((data) => {
              row[col] = val
              this.loading = false
          }).catch(({data}) => {
              this.dialog = true
              this.errorTitle = data.error
              this.errorMessages = []
              for (let err in data.errors) {
                  this.errorMessages.push(data.errors[err][0])
              }
              row[col] = initVal
              this.loading = false
          })
      },
      edit(value) {
        this.$router.push({name: 'rebit-accounts.edit', params: {id: value.id}})
      },
      syncBalance(row) {
        this.syncing = true
        new RebitAccount({
            id: row.id
        }).syncRealBalance((balance) => {
            this.syncing = false
            row['real_balance'] = balance
        }).catch(({error}) => {
            this.$q.notify({
                message: 'An error has occurred!',
                color: 'negative',
                icon: 'fa fa-exclamation-triangle'
            })
            this.syncing = false
        })
      },
      myTweak (offset) {
        return { minHeight: offset ? `calc(35vh - ${offset}px)` : '35vh' }
      },
      details(props) {
        props.expand = !props.expand
      },
      deleteRow(row) {
        this.selectedRows.push(row);
        this.deleteRows();
      },
      deleteRows() {
        this.$q.dialog({
          title: 'Delete information',
          message: 'Are your sure?',
          ok: 'Yes',
          cancel: 'No'
        }).onOk(() => {
          this.$q.loading.show()
          this.selectedRows.map((row) => {
            let rebit_account = new RebitAccount({id: row.id});
            rebit_account.destroy(() => {
              this.serverData = this.serverData.filter(record => record.id !== row.id);
              this.selectedRows = this.selectedRows.filter(member => member.id !== row.id);
              if(!this.selectedRows.length){
                this.$q.loading.hide()
              }
            }).catch(({data}) => {
              this.$q.notify(data.error)
              this.$q.loading.hide()
            })
          });
        }).onCancel(() => {
          this.$q.loading.hide()
        })
      },
    },
    beforeCreate() {
      if(! (this.$auth.can('rebit-accounts.list') || this.$auth.can('rebit-accounts.view_filtered_and_edit_real_balance')) ) {
        this.$router.push({name: 'dashboard'})
      }
    },
    mounted() {
      if (! this.currencies.length) {
        this.loading = true
        this.set_currencies().then(() => {this.loading = false}).catch(() => {this.loading = false})
      }
      // once mounted, we need to trigger the initial server data fetch
      if(this.$router.currentRoute.query.q !== undefined) {
        this.filter = this.$router.currentRoute.query.q
      } else {
        this.all({
            pagination: this.serverPagination,
            filter: this.filter
        })
      }
    },
    components: {
        TotalBalances,
        RebitAccountDetails
    }
  }
</script>
